import { useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import RumClient from './RumClient';
import { AppContext } from '../contexts/AppContext';
import { IdRegex, IdType, getIdType, getPathId } from './RumUtils';

// Create list of filters that remove unique IDs in the URL path
// Ensure new filters are placed in order otherwise pattern matching may not work
// due to previous substitutions
const filterPageView = (
  path: string,
): {
  pageId: string;
  contentId?: string;
  collectionId?: string;
  postId?: string;
  suggestionId?: string;
} => {
  let pageId = path;

  // Filter UIDs from the URL path
  const id = getPathId(path);

  pageId = pageId.replace(IdRegex, '\/UUID');
  const idType = getIdType(path);
  switch (idType) {
    case IdType.ContentId:
      return { pageId, contentId: id };
    case IdType.CollectionId:
      return { pageId, collectionId: id };
    case IdType.PostId:
      return { pageId, postId: id };
    case IdType.SuggestionId:
      return { pageId, suggestionId: id };
  }
  // @ts-expect-error TS7027 - strict migration
  return { pageId };
};
const WINDOW_EVENT = 'securitypolicyviolation';
function recordCspViolation(event: SecurityPolicyViolationEvent) {
  const {
    violatedDirective,
    documentURI,
    blockedURI,
    originalPolicy,
    referrer,
    statusCode,
    effectiveDirective,
    disposition,
  } = event;

  RumClient.recordEvent('csp_violation', {
    violatedDirective,
    documentURI,
    blockedURI,
    originalPolicy,
    referrer,
    statusCode,
    effectiveDirective,
    disposition,
  });
}

const RumManager = () => {
  const rumClient = RumClient.create();
  const appContext = useContext(AppContext);
  const userProfile = appContext?.authnInfo?.userProfile;

  // When the signed-in user resolves, then attribute the session to them.
  useEffect(() => {
    if (userProfile?.userId) {
      RumClient.addSessionAttributes({ userId: userProfile.userId });
    }
  }, [userProfile]);

  // Manually record page views to merge out unique pages
  const location = useLocation();
  useEffect(() => {
    const pageInfo = filterPageView(location.pathname);

    // When the route changes, then keep the session url current.
    RumClient.addSessionAttributes({ url: `${window.location.origin}${location.pathname}` });

    RumClient.recordPageView(
      pageInfo.pageId,
      userProfile,
      pageInfo.contentId,
      pageInfo.collectionId,
      pageInfo.postId,
      pageInfo.suggestionId,
    );
  }, [location, userProfile]);

  useEffect(() => {
    window.addEventListener(WINDOW_EVENT, recordCspViolation);

    return () => {
      window.addEventListener(WINDOW_EVENT, recordCspViolation);
    };
  });

  return null;
};

export default RumManager;
